import React from 'react';
import styled from 'styled-components';

import { BuildOutlined, DeleteOutlined } from '@mui/icons-material';
import { alpha, Chip, IconButton } from '@mui/material';
import { AITool, ToolDrawerOpen, ToolModification } from './tool.types';
import { getToolDefault } from './tool.defaults';
import { flowActions } from '@store/index';
import { useDispatch } from 'react-redux';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1;
  border-radius: 8px;
  padding: 0.5rem;

  &:hover {
    background-color: ${({ theme }) => alpha(theme.background.light, 0.4)};
    cursor: pointer;
  }
`;

const Tool = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

export const ToolIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  background-color: ${({ theme }) => theme.background.light};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.line.light};

  height: 40px;
  width: 40px;
  min-width: 40px;

  svg {
    font-size: 24px;
    color: ${({ theme }) => theme.text.standard};
  }
`;

const ToolDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  overflow: hidden;
  margin-right: 1rem;

  .name {
    font-size: 15px;
    color: ${({ theme }) => theme.text.standard};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }

  .description {
    font-size: 13px;
    color: ${({ theme }) => theme.text.subtext};
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    white-space: nowrap;
    min-width: 0;
  }
`;

interface Props {
  tool: AITool;
  setOpenTool: React.Dispatch<React.SetStateAction<ToolDrawerOpen | null>>;
}

export const AssistantTool: React.FC<Props> = ({ tool, setOpenTool }) => {
  const dispatch = useDispatch();

  const { name } = getToolDefault(tool.execution);

  const editTool = () => {
    setOpenTool({ tool, modification: ToolModification.EDIT });
  };

  const deleteTool = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation();
    dispatch(flowActions.setDeleteAssistantTool({ assistantId: tool.assistant_id, tool, type: tool.type }));
  };

  return (
    <Container onClick={editTool}>
      <Tool>
        <ToolIcon>
          <BuildOutlined />
        </ToolIcon>
        <ToolDetails>
          <p className='name'>{tool.name}</p>
          <p className='description'>{tool.description}</p>
        </ToolDetails>
      </Tool>
      <Chip label={name} size='small' variant='outlined' />
      <IconButton sx={{ marginLeft: '0.5rem' }} onClick={(e) => deleteTool(e)}>
        <DeleteOutlined />
      </IconButton>
    </Container>
  );
};
